import React, { useState } from 'react';
import { Settings, X, Eye, EyeOff, GripVertical, Lock, Unlock, RotateCcw, Check } from 'lucide-react';

/**
 * ColumnSelectorPanel - User-configurable column settings
 * Allows show/hide, reorder, resize, and freeze columns
 */
const ColumnSelectorPanel = ({
  columns = [],
  onChange = null,
  onReset = null,
  onSave = null,
  theme = 'light',
  isOpen = false,
  onClose = null
}) => {
  const isDark = theme === 'dark';
  const [localColumns, setLocalColumns] = useState(columns);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local columns when prop changes
  React.useEffect(() => {
    setLocalColumns(columns);
    setHasChanges(false);
  }, [columns]);

  // Toggle column visibility
  const toggleVisibility = (field) => {
    setLocalColumns(prev => prev.map(col => 
      col.field === field ? { ...col, visible: !col.visible } : col
    ));
    setHasChanges(true);
  };

  // Toggle column frozen state
  const toggleFrozen = (field) => {
    setLocalColumns(prev => prev.map(col => 
      col.field === field ? { ...col, frozen: !col.frozen } : col
    ));
    setHasChanges(true);
  };

  // Handle drag start
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newColumns = [...localColumns];
    const draggedColumn = newColumns[draggedIndex];
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(index, 0, draggedColumn);
    
    // Update order property
    newColumns.forEach((col, i) => col.order = i);
    
    setLocalColumns(newColumns);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Apply changes
  const applyChanges = () => {
    onChange?.(localColumns);
    onSave?.(localColumns);
    setHasChanges(false);
  };

  // Reset to default
  const resetToDefault = () => {
    onReset?.();
    setHasChanges(false);
  };

  // Show all columns
  const showAll = () => {
    setLocalColumns(prev => prev.map(col => ({ ...col, visible: true })));
    setHasChanges(true);
  };

  // Hide all columns
  const hideAll = () => {
    setLocalColumns(prev => prev.map(col => ({ ...col, visible: false })));
    setHasChanges(true);
  };

  if (!isOpen) return null;

  const visibleCount = localColumns.filter(c => c.visible !== false).length;
  const frozenCount = localColumns.filter(c => c.frozen).length;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className={`fixed right-0 top-0 bottom-0 w-96 z-50 flex flex-col shadow-2xl ${
          isDark ? 'bg-[#0e0e10]' : 'bg-white'
        }`}
        data-testid="column-selector-panel"
      >
        {/* Header */}
        <div className={`shrink-0 px-5 py-4 border-b flex items-center justify-between ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <Settings size={20} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
            <div>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Column Settings</h2>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                {visibleCount} visible, {frozenCount} frozen
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Quick Actions */}
        <div className={`shrink-0 px-5 py-3 border-b flex items-center gap-2 ${isDark ? 'border-[#222]' : 'border-gray-100'}`}>
          <button
            onClick={showAll}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              isDark ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20' : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            Show All
          </button>
          <button
            onClick={hideAll}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'
            }`}
          >
            Hide All
          </button>
          <div className="flex-1" />
          <button
            onClick={resetToDefault}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              isDark ? 'bg-white/5 text-slate-400 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <RotateCcw size={12} />
            Reset
          </button>
        </div>

        {/* Column List */}
        <div className="flex-1 overflow-y-auto p-3">
          {localColumns.map((column, index) => (
            <div
              key={column.field}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all cursor-move ${
                draggedIndex === index 
                  ? isDark ? 'bg-blue-500/20 ring-1 ring-blue-500' : 'bg-blue-50 ring-1 ring-blue-300'
                  : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
              } ${column.visible === false ? 'opacity-50' : ''}`}
              data-testid={`column-item-${column.field}`}
            >
              {/* Drag Handle */}
              <GripVertical size={16} className={isDark ? 'text-slate-600' : 'text-gray-400'} />
              
              {/* Column Name */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {column.header}
                </p>
                <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {column.field} • {column.dataType}
                </p>
              </div>
              
              {/* Freeze Toggle */}
              <button
                onClick={() => toggleFrozen(column.field)}
                className={`p-1.5 rounded transition-colors ${
                  column.frozen
                    ? isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-50 text-amber-600'
                    : isDark ? 'text-slate-600 hover:text-slate-400' : 'text-gray-400 hover:text-gray-600'
                }`}
                title={column.frozen ? 'Unfreeze column' : 'Freeze column'}
              >
                {column.frozen ? <Lock size={14} /> : <Unlock size={14} />}
              </button>
              
              {/* Visibility Toggle */}
              <button
                onClick={() => toggleVisibility(column.field)}
                className={`p-1.5 rounded transition-colors ${
                  column.visible !== false
                    ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-600'
                    : isDark ? 'text-slate-600 hover:text-slate-400' : 'text-gray-400 hover:text-gray-600'
                }`}
                title={column.visible !== false ? 'Hide column' : 'Show column'}
              >
                {column.visible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className={`shrink-0 px-5 py-4 border-t flex items-center justify-between ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
            Drag to reorder columns
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={applyChanges}
              disabled={!hasChanges}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasChanges
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600/50 text-white/70 cursor-not-allowed'
              }`}
            >
              <Check size={16} />
              Apply
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * ColumnSettingsButton - Trigger button for column settings
 */
export const ColumnSettingsButton = ({ onClick, theme = 'light' }) => {
  const isDark = theme === 'dark';
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
      data-testid="column-settings-btn"
    >
      <Settings size={16} />
      <span>Columns</span>
    </button>
  );
};

export default ColumnSelectorPanel;
